import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const selectedVariantId = this.dataset.selectedVariantId;
    if (selectedVariantId) return selectedVariantId;

    const formVariantId = /** @type {HTMLInputElement | null} */ (this.querySelector('product-form-component input[name="id"]'));
    if (formVariantId?.value) return formVariantId.value;

    const variantIdFromUrl = this.#getVariantIdFromUrl(this.refs.productCardLink.href);
    if (variantIdFromUrl) return variantIdFromUrl;

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#setInitialVariantState();
    this.#lockMobileCardGalleryToFeaturedImage();
    requestAnimationFrame(() => this.#lockMobileCardGalleryToFeaturedImage());
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested && isDesktopBreakpoint()) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #getVariantIdFromUrl(urlString) {
    if (!urlString) return null;

    try {
      return new URL(urlString).searchParams.get('variant');
    } catch {
      return null;
    }
  }

  #setInitialVariantState() {
    const initialVariantId =
      this.dataset.selectedVariantId ||
      this.querySelector('product-form-component input[name="id"]')?.getAttribute('value') ||
      this.#getVariantIdFromUrl(this.refs.productCardLink.href);

    if (initialVariantId) {
      this.dataset.selectedVariantId = initialVariantId;
    }

    const initialMediaId =
      this.dataset.selectedVariantMediaId ||
      this.refs.slideshow?.initialSlide?.getAttribute('slide-id') ||
      this.variantPicker?.selectedOption?.dataset.optionMediaId;

    if (initialMediaId) {
      this.dataset.selectedVariantMediaId = initialMediaId;
    }
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Mobile cards should stay on the product featured image only.
   * Removing non-first slides prevents lazy-loaded hover/carousel media from loading out of order.
   */
  #lockMobileCardGalleryToFeaturedImage() {
    if (mediaQueryLarge.matches) return;

    const slideshow = this.refs.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slideshow || !slides?.length) return;

    const firstSlide = slides[0];
    for (const slide of slides.slice(1)) {
      slide.remove();
    }

    firstSlide.hidden = false;
    firstSlide.setAttribute('aria-hidden', 'false');
    slideshow.setAttribute('disabled', 'true');

    const featuredMediaId = firstSlide.getAttribute('slide-id');
    if (featuredMediaId) {
      this.dataset.selectedVariantMediaId = featuredMediaId;
    }
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    if (event.detail.resource?.id) {
      this.dataset.selectedVariantId = event.detail.resource.id.toString();
    }

    const selectedMediaId = event.detail.resource?.featured_media?.id?.toString();
    if (selectedMediaId) {
      this.dataset.selectedVariantMediaId = selectedMediaId;
    }

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.#updateFeaturedImageUrl();
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages(event.detail.resource);
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[1] || priceContainers[0];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);
    const updatedPriceText = this.#getDisplayedPriceText(newPriceElement);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }

    this.#updateAddToCartPrice(updatedPriceText);
  }

  /**
   * Gets the currently displayed product price text from a price container.
   * Mirrors the exact server-rendered variant price instead of formatting it again client-side.
   * @param {Element | null} priceRoot
   * @returns {string}
   */
  #getDisplayedPriceText(priceRoot) {
    if (!(priceRoot instanceof Element)) return '';

    const displayedPrice =
      priceRoot.querySelector('.price__sale:not(.price__hidden) .price') ||
      priceRoot.querySelector('.price__regular:not(.price__hidden) .price') ||
      priceRoot.querySelector('.price-item--sale.price') ||
      priceRoot.querySelector('.price__sale .price') ||
      priceRoot.querySelector('.price__regular .price') ||
      priceRoot.querySelector('.price-item__group .price');

    return displayedPrice?.textContent?.trim() || '';
  }

  /**
   * Syncs the standalone add-to-cart button price on collection cards.
   * Product page buttons are not handled here because this code only runs inside product cards.
   * @param {string} priceText
   */
  #updateAddToCartPrice(priceText) {
    if (!priceText) return;

    const addToCartPrices = this.querySelectorAll('.buy-buttons-block .add-to-cart-text__price');
    for (const addToCartPrice of addToCartPrices) {
      addToCartPrice.textContent = priceText;
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');
    const selectedVariantId = responseProductCard?.dataset.selectedVariantId;
    const selectedVariantMediaId = responseProductCard?.dataset.selectedVariantMediaId;

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (selectedVariantId) {
      this.dataset.selectedVariantId = selectedVariantId;
    }

    if (selectedVariantMediaId) {
      this.dataset.selectedVariantMediaId = selectedVariantMediaId;
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  #updateFeaturedImageUrl() {
    if (!mediaQueryLarge.matches) return;

    const featuredMediaUrl = this.getAttribute('data-featured-media-url');
    if (!featuredMediaUrl) return;

    const selectedImageId = this.dataset.selectedVariantMediaId;
    if (selectedImageId) {
      const matchingSlide = this.querySelector(`slideshow-slide[slide-id="${selectedImageId}"] img`);
      if (matchingSlide instanceof HTMLImageElement) return;
    }

    const currentVisibleImage =
      this.querySelector('slideshow-slide[aria-hidden="false"] img') || this.querySelector('.card-gallery img');

    if (!(currentVisibleImage instanceof HTMLImageElement)) return;

    currentVisibleImage.srcset = featuredMediaUrl;
    if (!currentVisibleImage.getAttribute('src') || currentVisibleImage.getAttribute('src')?.includes('/cdn/shop/files/')) {
      currentVisibleImage.src = featuredMediaUrl;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    this.#toggleAddToCartButton(event.detail.resource?.available === true);
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('product-form-component [ref="addToCartButton"], .add-to-cart__button button')
    );

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages(variant = null) {
    if (!mediaQueryLarge.matches) return;

    const { slideshow } = this.refs;
    const selectedImageId =
      variant?.featured_media?.id?.toString() ||
      this.dataset.selectedVariantMediaId ||
      this.variantPicker?.selectedOption?.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      this.dataset.selectedVariantMediaId = selectedImageId;
      const { slides = [] } = slideshow.refs;
      let hasMatchingSlide = false;

      for (const slide of slides) {
        const slideId = slide.getAttribute('slide-id');
        const isSelectedSlide = slideId === selectedImageId;

        if (isSelectedSlide) {
          hasMatchingSlide = true;
        }

        slide.hidden = !isSelectedSlide;
      }

      if (!hasMatchingSlide) {
        const initialSlide = slideshow.initialSlide || slides[0];
        if (initialSlide) {
          initialSlide.hidden = false;
        }
        return;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const selectedVariantMediaId = this.dataset.selectedVariantMediaId || this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (selectedVariantMediaId) {
      slideshow.select({ id: selectedVariantMediaId }, undefined, { animate: false });
      return;
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /** @type {HTMLInputElement | HTMLOptionElement | undefined} */
  #lastChangedOption;

  #handleCardVariantUpdate = this.#handleCardVariantUrlUpdate.bind(this);
  #handleBreakpointChange = () => {
    this.#syncResponsiveSizeControls();
  };

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    this.#syncResponsiveSizeControls();
    this.#lastChangedOption = this.#getLastActiveSelectedOption();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Updates responsive controls and card state after a variant update.
   */
  #handleCardVariantUrlUpdate() {
    this.#syncResponsiveSizeControls();
    this.#lastChangedOption = this.#getLastActiveSelectedOption();
  }

  /**
   * Updates linked responsive controls before the standard variant flow continues.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    let resolvedTarget = target;

    if (typeof target === 'string') {
      resolvedTarget = this.querySelector(`[data-option-value-id="${target}"]`) ?? target;
    }

    super.updateSelectedOption(target);

    const selectedOption =
      resolvedTarget instanceof HTMLSelectElement ? resolvedTarget.options[resolvedTarget.selectedIndex] : resolvedTarget;

    if (selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement) {
      if (selectedOption.dataset.optionRole === 'size' && selectedOption.dataset.optionValueId) {
        this.#syncResponsiveSizeValue(selectedOption.dataset.optionValueId);
      }

      if (!this.#isControlInactive(selectedOption)) {
        this.#lastChangedOption = selectedOption;
      }
    }
  }

  /**
   * Override the variantChanged method so the active option state is tracked for multi-option cards.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.#lastChangedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    super.variantChanged(event);
  }

  /**
   * Gets the currently active selected option while ignoring disabled responsive duplicates.
   * @returns {(HTMLInputElement | HTMLOptionElement)[]}
   */
  #getActiveSelectedOptions() {
    /** @type {(HTMLInputElement | HTMLOptionElement)[]} */
    const selectedOptions = [];

    const selectedCandidates = this.querySelectorAll(
      'fieldset input:checked[data-option-value-id], select option[selected][data-option-value-id]'
    );

    for (const candidate of selectedCandidates) {
      if (candidate instanceof HTMLInputElement) {
        if (!candidate.disabled) {
          selectedOptions.push(candidate);
        }
        continue;
      }

      if (candidate instanceof HTMLOptionElement) {
        const select = candidate.parentElement;
        if (select instanceof HTMLSelectElement && !select.disabled) {
          selectedOptions.push(candidate);
        }
      }
    }

    return selectedOptions;
  }

  /**
   * Gets the last active selected option in the picker.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  #getLastActiveSelectedOption() {
    const activeSelectedOptions = this.#getActiveSelectedOptions();
    return activeSelectedOptions[activeSelectedOptions.length - 1];
  }

  /**
   * Whether a control should be ignored because its responsive twin is currently active.
   * @param {HTMLInputElement | HTMLOptionElement} control
   * @returns {boolean}
   */
  #isControlInactive(control) {
    if (control instanceof HTMLInputElement) {
      return control.disabled;
    }

    const select = control.parentElement;
    return !(select instanceof HTMLSelectElement) || select.disabled;
  }

  /**
   * Keeps the size desktop buttons and mobile dropdown in sync.
   * @param {string} optionValueId
   */
  #syncResponsiveSizeValue(optionValueId) {
    if (!optionValueId) return;

    const desktopInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`fieldset[data-size-buttons] input[data-option-value-id="${optionValueId}"]`)
    );

    if (desktopInput) {
      super.updateSelectedOption(desktopInput);
    }

    const sizeSelects = this.querySelectorAll('select[data-size-dropdown]');
    for (const select of sizeSelects) {
      if (!(select instanceof HTMLSelectElement)) continue;

      const targetOption = Array.from(select.options).find((option) => option.dataset.optionValueId === optionValueId);
      if (!targetOption) continue;

      for (const option of select.options) {
        option.removeAttribute('selected');
      }

      targetOption.selected = true;
      targetOption.setAttribute('selected', 'selected');
      select.value = targetOption.value;
    }
  }

  /**
   * Enables the appropriate size control for the current breakpoint.
   */
  #syncResponsiveSizeControls() {
    const selectedSizeOption =
      this.querySelector('fieldset[data-size-buttons] input:checked[data-option-value-id]') ||
      this.querySelector('select[data-size-dropdown] option[selected][data-option-value-id]');

    const selectedSizeOptionId =
      selectedSizeOption instanceof HTMLInputElement || selectedSizeOption instanceof HTMLOptionElement
        ? selectedSizeOption.dataset.optionValueId
        : null;

    const isDesktop = mediaQueryLarge.matches;

    const sizeInputs = this.querySelectorAll('fieldset[data-size-buttons] input');
    for (const input of sizeInputs) {
      if (input instanceof HTMLInputElement) {
        input.disabled = !isDesktop;
      }
    }

    const sizeSelects = this.querySelectorAll('select[data-size-dropdown]');
    for (const select of sizeSelects) {
      if (select instanceof HTMLSelectElement) {
        select.disabled = isDesktop;
      }
    }

    if (selectedSizeOptionId) {
      this.#syncResponsiveSizeValue(selectedSizeOptionId);
    }
  }

  get selectedOption() {
    if (this.#lastChangedOption && !this.#isControlInactive(this.#lastChangedOption)) {
      return this.#lastChangedOption;
    }

    return this.#getLastActiveSelectedOption();
  }

  get selectedOptionsValues() {
    return this.#getActiveSelectedOptions().map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
